<template>
  <div class="report-filters">
    <q-btn
      class="report-filters__mobile-trigger"
      outline
      no-caps
      color="primary"
      icon="filter_alt"
      label="Параметры отчета"
      data-testid="report-filters-trigger"
      @click="openMobileFilters"
    />
    <button
      v-if="mobileOpen"
      class="report-filters__backdrop"
      type="button"
      aria-label="Закрыть параметры отчета"
      @click="closeMobileFilters"
    />
    <section
      class="report-filters__panel"
      :class="{ 'report-filters__panel--open': mobileOpen }"
      :aria-hidden="$q.screen.lt.sm && !mobileOpen"
      :aria-modal="$q.screen.lt.sm && mobileOpen ? 'true' : undefined"
      :role="$q.screen.lt.sm ? 'dialog' : 'region'"
      aria-labelledby="report-filters-title"
      data-testid="report-filters-panel"
    >
      <div class="report-filters__heading">
        <div>
          <h3 id="report-filters-title" class="report-filters__title">Параметры отчета</h3>
          <p class="report-filters__description">Период и единица измерения</p>
        </div>
        <q-btn
          class="report-filters__mobile-close"
          flat
          round
          icon="close"
          aria-label="Закрыть параметры отчета"
          @click="closeMobileFilters"
        />
      </div>

      <div class="report-filters__fields">
        <q-select
          dense
          outlined
          label="Быстрый период"
          :options="periodOptions"
          v-model="localFilters.periodPreset"
          @update:model-value="applyPreset"
        />
        <q-input dense outlined label="Дата начала" v-model="localFilters.start" :mask="dateInputMask" :rules="dateRules">
          <template v-slot:append>
            <q-icon name="event" class="cursor-pointer" role="button" tabindex="0" aria-label="Выбрать дату начала" @keydown.enter.prevent="$event.currentTarget.click()" @keydown.space.prevent="$event.currentTarget.click()">
              <q-popup-proxy cover transition-show="scale" transition-hide="scale">
                <q-date v-model="localFilters.start" :mask="quasarDateMask" @update:model-value="emitChange" />
              </q-popup-proxy>
            </q-icon>
          </template>
        </q-input>
        <q-input dense outlined label="Дата окончания" v-model="localFilters.end" :mask="dateInputMask" :rules="dateRules">
          <template v-slot:append>
            <q-icon name="event" class="cursor-pointer" role="button" tabindex="0" aria-label="Выбрать дату окончания" @keydown.enter.prevent="$event.currentTarget.click()" @keydown.space.prevent="$event.currentTarget.click()">
              <q-popup-proxy cover transition-show="scale" transition-hide="scale">
                <q-date v-model="localFilters.end" :mask="quasarDateMask" @update:model-value="emitChange" />
              </q-popup-proxy>
            </q-icon>
          </template>
        </q-input>
        <q-input
          dense
          outlined
          label="Единица измерения"
          model-value="руб."
          readonly
        >
          <q-tooltip>Отчет доступен только в рублях.</q-tooltip>
        </q-input>
      </div>

      <div class="report-filters__footer">
        <div>
        <div class="text-caption text-grey-7">
          Организация: текущая выбранная организация{{ currentOrgId ? ` #${currentOrgId}` : '' }}
        </div>
        <div v-if="periodError" class="text-negative text-caption q-mt-xs" role="alert" data-testid="report-period-error">
          {{ periodError }}
        </div>
        <div v-else-if="!currentOrgId" class="text-negative text-caption q-mt-xs">
          Текущая организация не выбрана
        </div>
      </div>
        <div class="report-filters__actions">
        <q-btn
          flat
          no-caps
          color="primary"
          icon="restart_alt"
          label="Сбросить фильтры"
          :disable="loading"
          @click="resetFilters"
        />
        <q-btn
          unelevated
          no-caps
          color="primary"
          icon="play_arrow"
          label="Сформировать"
          :loading="loading"
          :disable="Boolean(periodError) || !currentOrgId || loading"
          @click="submitGenerate"
        />
      </div>
      </div>
    </section>
  </div>
</template>

<script>
import moment from 'moment'
import {
  DATE_DISPLAY_FORMAT,
  DATE_INPUT_MASK,
  QUASAR_DATE_MASK,
  parseDate
} from '../../../../services/dateFormat'

const periodOptions = [
  { label: 'Месяц', value: 'month' },
  { label: 'Квартал', value: 'quarter' },
  { label: 'Полугодие', value: 'halfyear' },
  { label: 'Год', value: 'year' },
  { label: 'Произвольный период', value: 'custom' }
]

const measureOptions = [
  { label: 'руб.', value: 'rub' }
]

export default {
  name: 'ReportFilters',
  props: {
    modelValue: {
      type: Object,
      required: true
    },
    report: {
      type: Object,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    currentOrgId: {
      type: [String, Number],
      default: null
    }
  },
  emits: ['update:modelValue', 'generate', 'reset', 'changed'],
  data() {
    return {
      localFilters: this.cloneFilters(this.modelValue),
      suppressCustomPreset: false,
      mobileOpen: false,
      periodOptions,
      measureOptions,
      dateInputMask: DATE_INPUT_MASK,
      quasarDateMask: QUASAR_DATE_MASK,
      dateRules: [
        (value) => parseDate(value).isValid() || 'Дата в формате ДД.ММ.ГГГГ'
      ]
    }
  },
  computed: {
    periodError() {
      const end = parseDate(this.localFilters.end)
      const normalizedStart = parseDate(this.localFilters.start)
      if (!normalizedStart.isValid() || !end.isValid()) {
        return 'Укажите корректные даты периода'
      }
      if (normalizedStart.isAfter(end)) {
        return 'Дата начала не может быть позже даты окончания'
      }
      return ''
    }
  },
  watch: {
    modelValue: {
      deep: true,
      handler(value) {
        this.localFilters = this.cloneFilters(value)
      }
    },
    'localFilters.start'() {
      if (this.suppressCustomPreset) {
        return
      }
      this.localFilters.periodPreset = { label: 'Произвольный период', value: 'custom' }
      this.emitChange()
    },
    'localFilters.end'() {
      if (this.suppressCustomPreset) {
        return
      }
      this.localFilters.periodPreset = { label: 'Произвольный период', value: 'custom' }
      this.emitChange()
    }
  },
  mounted() {
    window.addEventListener('keydown', this.onKeydown)
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.onKeydown)
  },
  methods: {
    cloneFilters(value) {
      return {
        start: value.start,
        end: value.end,
        periodPreset: value.periodPreset || periodOptions[1],
        measure: value.measure || measureOptions[0]
      }
    },
    emitChange() {
      const next = this.cloneFilters(this.localFilters)
      this.$emit('update:modelValue', next)
      this.$emit('changed', next)
    },
    applyPreset(option) {
      const selected = option || periodOptions[1]
      const now = moment()
      this.suppressCustomPreset = true
      if (selected.value === 'month') {
        this.localFilters.start = now.clone().startOf('month').format(DATE_DISPLAY_FORMAT)
        this.localFilters.end = now.clone().endOf('month').format(DATE_DISPLAY_FORMAT)
      } else if (selected.value === 'quarter') {
        this.localFilters.start = now.clone().startOf('quarter').format(DATE_DISPLAY_FORMAT)
        this.localFilters.end = now.clone().endOf('quarter').format(DATE_DISPLAY_FORMAT)
      } else if (selected.value === 'halfyear') {
        const firstHalf = now.month() < 6
        this.localFilters.start = now.clone().month(firstHalf ? 0 : 6).startOf('month').format(DATE_DISPLAY_FORMAT)
        this.localFilters.end = now.clone().month(firstHalf ? 5 : 11).endOf('month').format(DATE_DISPLAY_FORMAT)
      } else if (selected.value === 'year') {
        this.localFilters.start = now.clone().startOf('year').format(DATE_DISPLAY_FORMAT)
        this.localFilters.end = now.clone().endOf('year').format(DATE_DISPLAY_FORMAT)
      }
      this.localFilters.periodPreset = selected
      this.emitChange()
      this.$nextTick(() => {
        this.suppressCustomPreset = false
      })
    },
    resetFilters() {
      const defaults = {
        start: moment().startOf('quarter').format(DATE_DISPLAY_FORMAT),
        end: moment().endOf('quarter').format(DATE_DISPLAY_FORMAT),
        periodPreset: periodOptions[1],
        measure: measureOptions[0]
      }
      this.localFilters = defaults
      this.$emit('reset', defaults)
      this.emitChange()
    },
    openMobileFilters() {
      this.mobileOpen = true
      this.$nextTick(() => {
        window.requestAnimationFrame(() => {
          const closeButton = this.$el.querySelector('.report-filters__mobile-close')
          if (closeButton) closeButton.focus()
        })
      })
    },
    closeMobileFilters() {
      this.mobileOpen = false
    },
    submitGenerate() {
      this.closeMobileFilters()
      this.$emit('generate')
    },
    onKeydown(event) {
      if (event.key === 'Escape' && this.mobileOpen) {
        this.closeMobileFilters()
      }
    }
  }
}
</script>

<style scoped>
.report-filters {
  min-width: 0;
}
.report-filters__panel {
  padding: var(--sid-space-4);
  border: 1px solid var(--sid-color-line);
  border-radius: var(--sid-radius-large);
  background: var(--sid-color-surface-0);
}
.report-filters__heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--sid-space-3);
  margin-bottom: var(--sid-space-3);
}
.report-filters__title,
.report-filters__description {
  margin: 0;
}
.report-filters__title {
  font-size: var(--sid-font-size-section);
  line-height: var(--sid-line-height-section);
}
.report-filters__description {
  margin-top: var(--sid-space-1);
  color: var(--sid-color-text-secondary);
}
.report-filters__fields {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--sid-space-3);
}
.report-filters__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sid-space-4);
  margin-top: var(--sid-space-3);
}
.report-filters__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: var(--sid-space-2);
}
.report-filters__mobile-trigger,
.report-filters__mobile-close,
.report-filters__backdrop {
  display: none;
}
@media (max-width: 1023px) {
  .report-filters__fields {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (max-width: 599px) {
  .report-filters__mobile-trigger {
    display: inline-flex;
    width: 100%;
    min-height: var(--sid-touch-target);
  }
  .report-filters__backdrop {
    position: fixed;
    z-index: 3999;
    inset: 0;
    display: block;
    width: 100%;
    border: 0;
    background: var(--sid-color-overlay);
  }
  .report-filters__panel {
    position: fixed;
    z-index: 4000;
    top: 0;
    right: 0;
    bottom: 0;
    width: min(24rem, 92vw);
    overflow-y: auto;
    border: 0;
    border-left: 1px solid var(--sid-color-line);
    border-radius: 0;
    box-shadow: var(--sid-elevation-3);
    visibility: hidden;
    transform: translateX(100%);
    transition: transform 160ms ease, visibility 160ms;
  }
  .report-filters__panel--open {
    visibility: visible;
    transform: translateX(0);
  }
  .report-filters__mobile-close {
    display: inline-flex;
  }
  .report-filters__fields {
    grid-template-columns: minmax(0, 1fr);
  }
  .report-filters__footer {
    align-items: stretch;
    flex-direction: column;
  }
  .report-filters__actions {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    width: 100%;
  }
  .report-filters__actions :deep(.q-btn) {
    width: 100%;
    min-height: 44px;
    margin: 0;
  }
}
</style>
